'use client';

import Image from 'next/image';
import { getImageUrl } from '@/lib/imageUrl';

const CDN_DESKTOP = getImageUrl("/vilvabusiness/website/common/global-partners/");
const CDN_MOBILE = getImageUrl("/vilvabusiness/website/common/global-partners/");

const getImageWithFallback = (fallback: string) => {
  return (e: any) => {
    if (!e.target.dataset.fallback) {
      e.target.src = fallback;
      e.target.dataset.fallback = "true";
    }
  };
};

type Partner = {
  src: string;
  mobileSrc: string;
  alt: string;
  href: string;
  // When true, the logo only exists in the desktop (global-partners) folder,
  // so the mobile <img> reuses the desktop asset instead of requesting the
  // non-existent swiper-logo/ variant (which 404s).
  mobileFromDesktop?: boolean;
};

const partners: Partner[] = [
  { src: 'phonepe.webp', mobileSrc: 'phonepe.webp', alt: 'PhonePe Logo', href: 'https://business.phonepe.com/register?referral-code=RF2405271212318567147728', mobileFromDesktop: true },
  { src: 'logo_instamojo_logo_2-desktop.webp', mobileSrc: 'logo_instamojo_logo_1-mobile.webp', alt: 'Instamojo Logo', href: 'https://www.instamojo.com/' },
  { src: 'logo_digitalocean_logo_1-desktop.webp', mobileSrc: 'logo_digitalocean_logo_2-mobile.webp', alt: 'DigitalOcean Logo', href: 'https://www.digitalocean.com/?refcode=08170100c08a&utm_campaign=Referral_Invite&utm_medium=Referral_Program&utm_source=CopyPaste' },
  { src: 'shiprocket.webp', mobileSrc: 'shiprocket.webp', alt: 'Shiprocket Logo', href: 'https://app.shiprocket.in/register?rcode=REFzyuiju&utm_source=refer-n-earn&utm_medium=web&utm_campaign=refer-n-earn&utm_term=43763', mobileFromDesktop: true },
  { src: 'nimbuspost-logo.svg', mobileSrc: 'nimbuspost-logo.svg', alt: 'Nimbuspost Logo', href: 'https://ship.nimbuspost.com/?ref=8741' },
  { src: 'aws-logo.svg', mobileSrc: 'aws-logo.svg', alt: 'Amazon Web Services Logo', href: 'https://aws.amazon.com/free/' },
  { src: 'logo_briskpe1_logo_1-desktop.webp', mobileSrc: 'logo_briskpe1_logo_2-mobile.webp', alt: 'Briskpe Logo', href: 'https://web.briskpe.com/auth/signup/?referralCode=CU827012' },
  // { src: 'cloudflare_logo_1.webp', mobileSrc: 'cloudflare_1.webp', alt: 'Cloudflare Logo', href: 'https://www.cloudflare.com/' },
  { src: 'meta-logo.svg', mobileSrc: 'meta-logo.svg', alt: 'Meta Logo', href: 'https://www.meta.com/', mobileFromDesktop: true },
  { src: 'nsdl-logo.svg', mobileSrc: 'nsdl-logo.svg', alt: 'NSDL Logo', href: 'https://nsdl.co.in/', mobileFromDesktop: true },
  { src: 'paysprint-logo.svg', mobileSrc: 'paysprint-logo.svg', alt: 'PaySprint Logo', href: 'https://paysprint.in/', mobileFromDesktop: true },
  { src: 'perplexity-logo.svg', mobileSrc: 'perplexity-logo.svg', alt: 'Perplexity Logo', href: 'https://www.perplexity.ai/', mobileFromDesktop: true },
  { src: 'redbus-logo.svg', mobileSrc: 'redbus-logo.svg', alt: 'RedBus Logo', href: 'https://www.redbus.in/', mobileFromDesktop: true },
  { src: 'razorpay-logo.svg', mobileSrc: 'razorpay-logo.svg', alt: 'Razorpay Logo', href: 'https://razorpay.com/', mobileFromDesktop: true },
  { src: 'solarwinds-logo.svg', mobileSrc: 'solarwinds-logo.svg', alt: 'SolarWinds Logo', href: 'https://www.solarwinds.com/', mobileFromDesktop: true },
];

const InterCustomerSlider = () => {
  // The list is already rendered twice, so a CSS marquee translating -50% is exactly the tween
  // GSAP used to run (scrollWidth / 2 over 40s) — same speed, but without loading GSAP or
  // measuring the DOM during hydration. `w-max` is required: see .marquee-track in globals.css.
  const duplicatedPartners = [...partners, ...partners];

  return (
    <div className="overflow-hidden relative z-20">
      <div
        className="marquee-track w-max flex items-center"
        style={{ animation: 'marqueeToLeft 40s linear infinite' }}
      >
        {duplicatedPartners.map((partner, index) => {
          const desktopUrl = `${CDN_DESKTOP}${partner.src}`;
          // Logos flagged mobileFromDesktop have no swiper-logo/ variant, so
          // point the mobile image at the desktop asset to avoid a 404.
          const mobileUrl = partner.mobileFromDesktop
            ? desktopUrl
            : `${CDN_MOBILE}${partner.mobileSrc}`;

          return (
            <div key={index} className="flex-shrink-0 mx-4">
              <a href={partner.href} target="_blank" rel="noopener noreferrer">

                {/* Desktop */}
                <Image
                  src={desktopUrl}
                  className="hidden md:block w-[100px] h-[100px] object-contain"
                  width={100}
                  height={100}
                  alt={partner.alt}
                  onError={getImageWithFallback(mobileUrl)}
                />

                {/* Mobile */}
                <Image
                  src={mobileUrl}
                  className="block md:hidden w-[120px] h-[80px] object-contain"
                  width={120}
                  height={80}
                  alt={partner.alt}
                  onError={getImageWithFallback(desktopUrl)}
                />

              </a>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InterCustomerSlider;